/**
 * Bar Chart Component
 *
 * Renders the bar payload returned by `chart_resolver.bar.resolve`.
 * Supports:
 *  - Optional target reference line (`meta.target_value`)
 *  - Simple Moving Average overlay (rows carry an `sma` field when active)
 *  - Multi-Y-axis overlays — secondary `series.y2` is rendered against a
 *    right-hand axis. We use a `ComposedChart` so we can mix bars and lines.
 *  - Mock data fallback when no live payload is available.
 */

import { useMemo } from 'react';
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import { CHART_COLORS } from '../constants';
import { generateMockData, MockBarData } from '../utils';
import { useIndicatorData, IndicatorDataPoint } from './useIndicatorData';
import { useDrilldown } from './useDrilldown';

/** 12000000 → "12M" — stops long values clipping against the axis edge. */
function compactNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

interface BarChartComponentProps {
  indicatorId?: number;
  /** Override the default 250 px chart height (used by the full-view dialog). */
  height?: number | string;
  /**
   * Relabel the x axis. A date bucket arrives as the first day of its period
   * ('2021-03-01'), which is not what anyone wants to read across sixty
   * columns; a caller that knows its axis is monthly can shorten it.
   */
  xTickFormatter?: (value: string) => string;
}

type BarRow = IndicatorDataPoint | MockBarData;

export function BarChartComponent({ indicatorId, height = 250, xTickFormatter }: BarChartComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);
  const isPreviewMode = indicatorId === undefined;

  const data: BarRow[] = useMemo(() => {
    if (live && Array.isArray(live.data) && live.data.length > 0) {
      return live.data;
    }
    // Mock data only for storybook/preview (no indicatorId). Real indicators
    // without data render an empty state instead of random numbers.
    if (isPreviewMode) return generateMockData('bar') as MockBarData[];
    return [];
  }, [live, isPreviewMode]);

  const series = live?.series;
  const meta = live?.meta;
  const targetValue = typeof meta?.target_value === 'number' ? meta.target_value : null;
  const drilldown = useDrilldown(meta?.drilldown_url);

  // X2 stack detection: when the resolver omits the default `value` series and
  // every remaining series carries a `stack_id`, each series entry becomes one
  // stacked bar (one per distinct X2 value).
  const seriesEntries = useMemo(() => Object.entries(series || {}), [series]);
  const stackEntries = useMemo(
    () => seriesEntries.filter(([key, def]) => key !== 'value' && Boolean((def as { stack_id?: string })?.stack_id)),
    [seriesEntries],
  );
  const isStacked = !series?.['value'] && stackEntries.length > 0;

  const valueLabel = series?.['value']?.label || 'Actual';
  const y2Entry = series?.['y2'];
  const hasY2 = Boolean(y2Entry) && data.some((row) => 'y2' in row);
  const hasSMA = Boolean(meta?.show_sma) && data.some((row) => 'sma' in row);

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      >
        {loading ? 'Loading…' : (live?.message || 'No data. Configure axes in the builder.')}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={12} interval={0} angle={-25} textAnchor="end" height={90} tickMargin={6}
               tickFormatter={xTickFormatter} />
        <YAxis yAxisId="left" fontSize={12} tickFormatter={compactNumber} width={56} />
        {hasY2 && (
          <YAxis yAxisId="right" orientation="right" fontSize={12}
                 tickFormatter={compactNumber} width={56} />
        )}
        <Tooltip />
        <Legend verticalAlign="top" height={28} wrapperStyle={{ paddingBottom: 4 }} />
        {isStacked ? (
          stackEntries.map(([key, def], idx) => (
            <Bar
              key={key}
              yAxisId="left"
              dataKey={key}
              stackId={(def as { stack_id?: string })?.stack_id || 'x2'}
              isAnimationActive={false}
              fill={CHART_COLORS[idx % CHART_COLORS.length]}
              name={(def as { label?: string })?.label || key}
              radius={idx === stackEntries.length - 1 ? [4, 4, 0, 0] : 0}
              cursor={meta?.drilldown_url ? 'pointer' : 'default'}
              onClick={(row: unknown) => {
                const name = (row as { name?: unknown })?.name;
                drilldown(name);
              }}
            />
          ))
        ) : (
          <Bar
            yAxisId="left"
            dataKey="value"
            isAnimationActive={false}
            fill={CHART_COLORS[0]}
            name={valueLabel}
            radius={[4, 4, 0, 0]}
            cursor={meta?.drilldown_url ? 'pointer' : 'default'}
            onClick={(row: unknown) => {
              const name = (row as { name?: unknown })?.name;
              drilldown(name);
            }}
          />
        )}
        {/* Optional pre-baked target column from mock fallback */}
        {!targetValue && data.some((row) => 'target' in row && row.target !== undefined) && (
          <Bar yAxisId="left" dataKey="target" isAnimationActive={false} fill={CHART_COLORS[1]} name="Target" radius={[4, 4, 0, 0]} />
        )}
        {targetValue !== null && (
          <ReferenceLine
            yAxisId="left"
            y={targetValue}
            stroke={CHART_COLORS[3]}
            strokeDasharray="4 4"
            label={{ value: `Target: ${targetValue}`, position: 'insideTopRight', fill: CHART_COLORS[3], fontSize: 11 }}
          />
        )}
        {hasSMA && (
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="sma"
            isAnimationActive={false}
            stroke={CHART_COLORS[4]}
            strokeWidth={2}
            dot={false}
            name="SMA"
          />
        )}
        {hasY2 && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="y2"
            isAnimationActive={false}
            stroke={CHART_COLORS[2]}
            strokeWidth={2}
            dot={false}
            name={y2Entry?.label || 'Series 2'}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
